import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateClientByIntegrationMutation } from '@app/api/Client/api';
import { useLazySearchClientsByIntegrationQuery } from '@app/api/Clients/api';
import { useLazyGetClientLeadForViewQuery } from '@app/api/Leads/api';
import { useGetUserSocialIntegrationsQuery } from '@app/api/SocialIntegrations/api';
import { LEAD_CARD_PATH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NClients } from '@app/types';

// Integration excluded from the "Написать первым" sender list: the 1C warehouse
// channel is not a real messaging integration.
const WAREHOUSE_1C_INTEGRATION = 'warehouse-1c';

// Radio value used when the user chooses to create a brand new client instead
// of picking one of the returned similar clients.
export const NEW_CLIENT_SELECTION = 'new';

export type LeadsWriteFirstSelection = number | typeof NEW_CLIENT_SELECTION;

export interface LeadsWriteFirstFormValues {
  integrationId: number;
  userName: string;
  clientName?: string | null;
}

interface UseLeadsWriteFirstModalParams {
  open: boolean;
  onClose: () => void;
}

export const useLeadsWriteFirstModal = ({
  open,
  onClose,
}: UseLeadsWriteFirstModalParams) => {
  const [form] = Form.useForm<LeadsWriteFirstFormValues>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const [similarClients, setSimilarClients] = useState<
    NClients.ModelClientLite[]
  >([]);
  const [selection, setSelection] =
    useState<LeadsWriteFirstSelection>(NEW_CLIENT_SELECTION);

  const { data: userIntegrations = [], isFetching: isIntegrationsLoading } =
    useGetUserSocialIntegrationsQuery(
      { ActiveBranchId },
      { skip: !open || !ActiveBranchId }
    );

  const [searchClients, { isFetching: isSearching }] =
    useLazySearchClientsByIntegrationQuery();
  const [createClientByIntegration, { isLoading: isCreating }] =
    useCreateClientByIntegrationMutation();
  const [getClientLeadForView, { isFetching: isOpeningLead }] =
    useLazyGetClientLeadForViewQuery();

  const integrationOptions = useMemo(
    () =>
      userIntegrations
        .filter(({ IdS }) => IdS !== WAREHOUSE_1C_INTEGRATION)
        .map(({ Id, Name }) => ({ value: Id, label: Name })),
    [userIntegrations]
  );

  const hasIntegrations = integrationOptions.length > 0;
  const isNewClient = selection === NEW_CLIENT_SELECTION;
  const selectedClientId =
    typeof selection === 'number' ? selection : undefined;

  const resetState = useCallback(() => {
    form.resetFields();
    setSimilarClients([]);
    setSelection(NEW_CLIENT_SELECTION);
  }, [form]);

  const handleCancel = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const findSimilarClients = useCallback(async () => {
    const { integrationId, userName, clientName } = form.getFieldsValue();

    if (!ActiveBranchId || !integrationId || !userName) return;

    try {
      const result = await searchClients({
        integrationId,
        userName,
        clientName: clientName || null,
        ActiveBranchId,
      }).unwrap();

      setSimilarClients(result);
      // Drop a stale client selection that is no longer part of the results.
      setSelection((prev) =>
        typeof prev === 'number' && !result.some(({ Id }) => Id === prev)
          ? NEW_CLIENT_SELECTION
          : prev
      );
    } catch (error) {
      showNotification(error, 'warning');
    }
  }, [ActiveBranchId, form, searchClients, showNotification]);

  const handleFinish = useCallback(
    async ({
      integrationId,
      userName,
      clientName,
    }: LeadsWriteFirstFormValues) => {
      if (!ActiveBranchId || !integrationId || !userName) return;

      try {
        const lead = isNewClient
          ? await createClientByIntegration({
              ActiveBranchId,
              data: {
                IntegrationId: integrationId,
                Phone: userName,
                ClientName: clientName || null,
              },
            }).unwrap()
          : selectedClientId
          ? await getClientLeadForView({
              ActiveBranchId,
              clientId: selectedClientId,
              integrationId,
            }).unwrap()
          : undefined;

        if (!lead?.Id) return;

        navigate(`${LEAD_CARD_PATH}?leadId=${lead.Id}&source=registry`);
        resetState();
        onClose();
      } catch (error) {
        showNotification(error, 'warning');
      }
    },
    [
      ActiveBranchId,
      createClientByIntegration,
      getClientLeadForView,
      isNewClient,
      navigate,
      onClose,
      resetState,
      selectedClientId,
      showNotification,
    ]
  );

  const isSubmitting = isCreating || isOpeningLead;
  const isSubmitDisabled =
    !hasIntegrations || (!isNewClient && !selectedClientId);

  return {
    form,
    integrationOptions,
    hasIntegrations,
    isIntegrationsLoading,
    similarClients,
    selection,
    isSearching,
    isSubmitting,
    isSubmitDisabled,
    onSelectionChange: setSelection,
    onFieldChange: findSimilarClients,
    onFinish: handleFinish,
    submit: form.submit,
    close: handleCancel,
  };
};
